<template>

    <div class="flex items-center justify-between mb-8">
        <page-title :title="set.name"></page-title>
    </div>

    <!-- Option Info Section -->
    <div class="my-2">
        <div class="md:flex bg-minplan-beige text-minplan-blue">
            <div class="flex w-full md:w-2/4">
                <div class="flex p-4 md:py-4 md:px-8 w-full">
                    <div>
                        <InformationCircleIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                        <button
                            @click="$inertia.visitInModal(route('advisors.option-sets.edit', set.uuid))"
                            type="button" class="block">
                            <PencilIcon
                                class="mr-2 mt-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige" />
                        </button>
                    </div>
                    <div>
                        <span class="truncate block w-full font-bold">{{ $t("options.infotext") }}</span>
                        <p class="text-sm">{{ set.description }}</p>
                    </div>
                </div>
            </div>
            <div class="flex p-4 md:py-4 md:px-8 w-full md:w-2/4">
                <div>
                    <PhotographIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                    <button
                        @click="$inertia.visitInModal(route('advisors.option-sets.edit-images', set.uuid))"
                        type="button" class="block">
                        <PencilIcon class="mr-2 mt-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige" />
                    </button>
                </div>
                <div>
                    <span class="truncate block w-full font-bold">{{ $t("options.infoimages") }}</span>
                    <p class="text-minplan-darkgreybeige text-sm">
                        <span v-if="set.images.length === 0">{{ $t("options.infoimagesText") }}</span>
                        <img v-for="image in set.images"  
                            :key="image.i" 
                            :src="image" 
                            alt="" 
                            class="w-32 h-full flex-shrink-0 object-cover object-left-top inline-block m-2 cursor-pointer" 
                            @click="$inertia.visitInModal(route('advisors.option-sets.edit-images', set.uuid))" />
                    </p>
                </div>
            </div>
        </div>
    </div>

    <!-- Info Bar Section -->
    <div class="my-2 pt-2 pb-3 flex justify-between items-center border-b">
        <div class="flex">
            <p class="flex items-center text-sm text-gray-500">
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" :class="[set.state === 'active' ? 'bg-lime-100 text-lime-800' : 'bg-gray-100', 'text-gray-800']">
                    {{ $t("states." + set.state) }}
                </span>
            </p>
        </div>

        <div class="flex">

            <button type="button" class="flex items-center text-sm text-gray-500"
                    @click="$inertia.visitInModal(route('advisors.option-sets.edit.state', {optionSet: set.uuid}))">
                <TagIcon class="h-5 w-5 text-minplan-darkgreybeige" />
                <span class="hidden md:flex ml-1">
                    {{ $t("options.stateChange") }}
                </span>
            </button>

        </div>
    </div>

    <!-- Options Section -->

    <div class="flex justify-between items-center mt-6">
        <div>
            <h2 class="font-bold uppercase tracking-wider">{{ $t("options.options") }}</h2>
        </div>
        <div>
            <secondary-button v-if="!sorting" @click="sorting = true" class="mr-3">
                <SwitchVerticalIcon class="-ml-1 md:mr-2 h-5 w-5 text-white md:text-minplan-darkgreybeige" />
                <span class="hidden md:inline font-bold">{{ $t("options.changeOrder") }}</span>
            </secondary-button>
            <secondary-button v-if="sorting" @click="cancelOrder()" class="mr-3">
                <XIcon class="-ml-1 md:mr-2 h-5 w-5 text-white md:text-minplan-darkgreybeige" />
                <span class="hidden md:inline font-bold">{{ $t("actions.cancel") }}</span>
            </secondary-button>
            <primary-button v-if="sorting" @click="saveOrder()">
                <CheckIcon class="-ml-1 md:mr-2 h-5 w-5 text-white md:text-minplan-darkgreybeige" />
                <span class="hidden md:inline font-bold">{{ $t("actions.save") }}</span>
            </primary-button>
            <primary-button v-if="!sorting"
                            @click="$inertia.visitInModal(route('advisors.option.create', {optionSet: set.uuid}))">
                <PlusSmIcon class="-ml-1 md:mr-2 h-5 w-5 text-white md:text-minplan-darkgreybeige" />
                <span class="hidden md:inline font-bold">{{ $t("options.newOption") }}</span>
            </primary-button>
        </div>
    </div>

    <div class="min-w-full mt-2 md:mt-4">
        <div class="flex p-2 justify-between bg-minplan-lightgreybeige">
            <div v-if="sorting" class="w-10 ml-2"></div>
            <div
                class="w-1/4 px-1 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-minplan-beige uppercase tracking-wider">
                {{ $t("options.optionName") }}
            </div>
            <div
                class="w-1/6 px-1 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-minplan-beige uppercase tracking-wider text-right">
                {{ $t("options.price") }}
            </div>
            <div
                class="w-1/6 px-1 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-minplan-beige uppercase tracking-wider text-center">
                {{ $t("options.images") }}
            </div>
            <div class="w-1/4"></div>
        </div>
        <SlickList axis="y" v-model:list="form.options" use-drag-handle tag="div">
            <SlickItem class="flex algin-center justify-between border border-gray-200 bg-white mt-3 mb-3 p-2"
                       v-for="(option, i) in form.options"
                       :key="option.uuid"
                       tag="div"
                       :index="i">
                <div v-if="sorting" class="w-10 flex items-center ml-2">
                    <DragHandle />
                </div>
                <div class="w-1/4 px-1 md:px-6 py-2 md:py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {{ option.title }}
                </div>
                <div
                    class="w-1/6 px-1 md:px-6 py-2 md:py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                    {{ option.cost }} <span v-if="option.unit">/ {{ option.unit }}</span>
                </div>
                <div
                    class="w-1/6 px-1 md:px-6 py-2 md:py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-center">
                    {{ option.imageCount }}
                </div>
                <div class="w-1/4 px-1 md:px-6 py-2 md:py-3 whitespace-nowrap text-sm text-right font-medium">
                        <span
                            class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" :class="[option.state === 'active' ? 'bg-lime-100 text-lime-800' : 'bg-gray-100', 'text-gray-800']">
                            {{ $t("states." + option.state) }}
                        </span>
                    <button type="button"
                            @click="$inertia.visitInModal(route('advisors.option.edit', {optionSet: set.uuid, option: option.uuid}))"
                            class="float-right block">
                        <PencilIcon class="ml-4 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige" />
                    </button>
                    <button type="button"
                            @click="$inertia.visitInModal(route('advisors.option.edit-images', {optionSet: set.uuid, option: option.uuid}))"
                            class="float-right block">
                        <PhotographIcon class="ml-4 mr-2 h-5 w-5 text-gray-500 float-right" />
                    </button>
                    <button
                        type="button"
                        @click="$inertia.visitInModal(route('advisors.option.delete', {optionSet: set.uuid, option: option.uuid}))"
                        class="float-right block">
                        <TrashIcon class="ml-4 mr-2 h-5 w-5 text-gray-500 float-right" />
                    </button>
                </div>
            </SlickItem>
        </SlickList>
    </div>


</template>

<script>
import MainLayout from "@/Layouts/MainLayout";
import PageTitle from "@/Shared/Components/PageTitle";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import {
    CheckIcon,
    InformationCircleIcon,
    PencilIcon,
    PhotographIcon,
    SwitchVerticalIcon,
    TagIcon,
    TrashIcon,
    XIcon,
    PlusSmIcon
} from "@heroicons/vue/outline";
import DragHandle from "@/Shared/Components/DragHandle";
import SecondaryButton from "@/Jetstream/SecondaryButton";
import { SlickItem, SlickList } from "vue-slicksort";
import { useForm } from "@inertiajs/inertia-vue3";

export default {
    props: ["set", "options"],
    layout: MainLayout,
    components: {
        SecondaryButton,
        PageTitle,
        PrimaryButton,
        InformationCircleIcon,
        PencilIcon,
        TagIcon,
        PhotographIcon,
        TrashIcon,
        CheckIcon,
        SwitchVerticalIcon,
        XIcon,
        PlusSmIcon,
        DragHandle,
        SlickItem,
        SlickList
    },
    data() {
        return {
            sorting: false,
            form: useForm({
                options: this.options
            })
        };
    },
    methods: {
        cancelOrder() {
            this.form.reset();
            this.sorting = false;
        },
        saveOrder() {
            this.form.put(this.route("advisors.option.sort", this.set.uuid), {
                preserveScroll: true,
                preserveState: false,
                onSuccess: () => {
                    this.$notify({
                        group: "success",
                        title: "actions.success",
                        text: "options.orderUpdated"
                    }, 2000);
                    this.sorting = false;
                }
            });
        }
    }
};
</script>

<style scoped>

</style>
